import React, { useEffect, useRef, useState } from 'react';

import AgreeCheckBox from './AgreeCheckbox';
import Button from './Button';
import CardBackground from '../../../_shared/CardBackground';
import LoadingCircle from '../../../_shared/LoadingCircle';
import MessageBubble from '../../../_shared/MessageBubble';
import Modal from '../../../_shared/Modal';
import TermsCard from './TermsCard';

import useRemoteCopy from '../../../../hooks/useRemoteCopy';

import { makeStyles } from '@mui/styles';

const useStyles = makeStyles({
    root: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        borderRadius: '16px',
        height: 'fit-content',
        margin: '0 auto 50px auto',
        padding: '50px 0 0 0'
    },
    title: {
        color: '#7378E8',
        fontFamily: 'Manrope',
        fontSize: '1.5rem',
        fontWeight: 800,
        letterSpacing: '0.94px',
        margin: '2.375rem 0 0'
    },
    label: {
        color: '#1F1F51',
        fontFamily: 'Manrope',
        fontSize: '1.125rem',
        fontWeight: 600,
        letterSpacing: 0,
        lineHeight: '2rem',
        margin: '1.75rem 0 0.5rem'
    },
    description: {
        color: '#1F1F51',
        fontFamily: 'Manrope',
        fontSize: '1rem',
        fontWeight: 500,
        letterSpacing: 0,
        lineHeight: '1.6875rem'
    },
    termsBody: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto',
        width: '75%',
        gap: '2rem'
    }
});

type TermsOfServiceSection = {
    body: string[];
    title: string;
};

type V2TermsOfServiceCopy = {
    sections: TermsOfServiceSection[];
    title: string;
};

type BodyProps = {
    agreed: boolean;
    profileName: string;
    setIcebreaker: () => void;
    submitHandler: () => void;
    toggleAgreed: () => void;
};

const Body: React.FC<BodyProps> = ({
    agreed,
    profileName,
    setIcebreaker,
    submitHandler,
    toggleAgreed
}) => {
    const classes = useStyles();
    const refetchCount = useRef(0);

    const [isErrored, setIsErrored] = useState(false);

    const v2_terms_of_service = useRemoteCopy('v2_terms_of_service');
    const welcome = useRemoteCopy('welcome_to_your_samaritan_membership');

    const termsCopy = v2_terms_of_service.remoteCopy as V2TermsOfServiceCopy | null;

    const bubbleMessage = `Can we run through and make sure you agree to Samaritan’s terms of services?`;

    const renderTermCards = () =>
        termsCopy?.sections.map((section, index) => (
            <TermsCard
                key={section.title ?? index}
                list={section.body}
                title={section.title}
            />
        )) ?? [];

    useEffect(() => {
        if (v2_terms_of_service.isError && refetchCount.current < 5) {
            refetchCount.current++;
            v2_terms_of_service.refetch();
        }
        if (refetchCount.current >= 5) {
            setIsErrored(true);
        }
    }, [v2_terms_of_service]);

    useEffect(() => {
        if (welcome.isError && refetchCount.current < 5) {
            refetchCount.current++;
            welcome.refetch();
        }
        if (refetchCount.current >= 5) {
            setIsErrored(true);
        }
    }, [welcome]);

    if (isErrored)
        return (
            <Modal open={isErrored}>
                <div style={{ padding: 20 }}>
                    <span className="font-lg">Something has gone wrong.</span>
                    <span>
                        Please refresh and try again. We apologize for this
                        inconvenience. If this error persists{' '}
                        <a href="mailto:support@samaritan.com">
                            contact support
                        </a>
                    </span>
                </div>
            </Modal>
        );

    if (!v2_terms_of_service.isLoaded || !welcome.isLoaded) {
        return <LoadingCircle />;
    }

    return (
        <div className={classes.root}>
            <CardBackground />
            <MessageBubble question={bubbleMessage} />
            <div className={classes.termsBody}>{renderTermCards()}</div>
            <AgreeCheckBox
                active={agreed}
                profileName={profileName}
                clickHandler={() => {
                    setIcebreaker();
                    toggleAgreed();
                }}
            />
            <Button active={agreed} clickHandler={submitHandler} />
        </div>
    );
};

export default Body;
